import { jsPDF } from 'jspdf'
import type { PocRecord } from '../types'
import { COUNTRIES } from '../data/countries'

/**
 * Export the updated FrankieOne POC Intake Form for Socure as a pre-filled PDF.
 * Reads from discovery_json.vendor_coordination.socure fields.
 */
export function exportSocureIntakePDF(poc: PocRecord): void {
  const doc = new jsPDF()
  const disc = poc.discovery_json as Record<string, any> || {}
  const vendorCoord = (disc.vendor_coordination || {}) as Record<string, any>
  const coord = vendorCoord.socure || {}

  const pageWidth = doc.internal.pageSize.getWidth()
  const margin = 16
  const contentWidth = pageWidth - margin * 2
  let y = 20

  // ===== Header =====
  doc.setFontSize(14)
  doc.setFont('helvetica', 'bold')
  doc.text('FrankieOne POC Intake Form', margin, y)
  y += 10

  // Helper: check page break
  function checkPage(needed = 12) {
    if (y > 275 - needed) { doc.addPage(); y = 20 }
  }

  // Helper: section header
  function sectionHeader(num: number, title: string) {
    checkPage(20)
    doc.setFontSize(11)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(44, 62, 80)
    doc.text(`${num}. ${title}`, margin, y)
    doc.setTextColor(0, 0, 0)
    doc.setFont('helvetica', 'normal')
    y += 7
  }

  // Helper: sub-label
  function subLabel(text: string) {
    doc.setFontSize(8)
    doc.setTextColor(100, 100, 100)
    const lines = doc.splitTextToSize(text, contentWidth)
    checkPage(lines.length * 4)
    doc.text(lines, margin, y)
    doc.setTextColor(0, 0, 0)
    y += lines.length * 4 + 2
  }

  // Helper: labeled field
  function labeledField(label: string, value: string | null | undefined, placeholder = '[Not provided]') {
    checkPage(12)
    doc.setFontSize(8)
    doc.setFont('helvetica', 'bold')
    doc.text(label, margin, y)
    doc.setFont('helvetica', 'normal')
    y += 4.5
    doc.setFontSize(9)
    const text = value?.trim() || placeholder
    const lines = doc.splitTextToSize(text, contentWidth)
    checkPage(lines.length * 4.5)
    doc.text(lines, margin, y)
    y += lines.length * 4.5 + 3
  }

  // Helper: simple table
  function simpleTable(headers: string[], rows: string[][], colWidths: number[]) {
    checkPage(8 + rows.length * 6)
    const rowHeight = 6
    doc.setFontSize(8)

    // Header row
    doc.setFont('helvetica', 'bold')
    doc.setFillColor(240, 240, 240)
    doc.rect(margin, y - 3.5, contentWidth, rowHeight, 'F')
    let x = margin
    for (let i = 0; i < headers.length; i++) {
      doc.text(headers[i], x + 1.5, y)
      x += colWidths[i]
    }
    y += rowHeight
    doc.setFont('helvetica', 'normal')

    // Data rows
    for (const row of rows) {
      checkPage(rowHeight)
      doc.setDrawColor(220, 220, 220)
      doc.line(margin, y - 3.5, margin + contentWidth, y - 3.5)
      x = margin
      for (let i = 0; i < row.length; i++) {
        const cellText = doc.splitTextToSize(row[i] || '', colWidths[i] - 3)
        doc.text(cellText[0] || '', x + 1.5, y)
        x += colWidths[i]
      }
      y += rowHeight
    }
    y += 4
  }

  // ===== 1. POC Overview =====
  sectionHeader(1, 'POC Overview')

  const customerName = coord.socure_customer || poc.company_name || ''
  const primaryContact = coord.socure_primary_contact || (disc.contact_name as string) || ''
  const internalOwner = coord.socure_internal_owner || poc.presales_owner || ''

  labeledField('Customer / Prospect:', customerName)
  labeledField('Primary contact:', primaryContact)
  labeledField('Requested start date:', coord.socure_start_date || null)
  labeledField('Requested go-live date:', coord.socure_golive_date || null)
  labeledField('Internal owner:', internalOwner)
  labeledField('Other stakeholders:', coord.socure_other_stakeholders || null)

  // ===== 2. POC Objective =====
  sectionHeader(2, 'POC Objective')
  subLabel('Please describe the purpose of the POC and what FrankieOne is being evaluated for.')

  labeledField('Business objective:', coord.socure_business_objective || null)
  labeledField('Use case:', coord.socure_use_case || null)
  labeledField('Product / workflow in scope:', coord.socure_product_workflow || coord.socure_products || null)
  labeledField('Key decision this POC is intended to support:', coord.socure_key_decision || null)

  // ===== 3. Projected Volumes =====
  sectionHeader(3, 'Projected Volumes')
  subLabel('Please provide expected volumes for the POC and expected production volumes if the POC is successful.')

  // Build volume from existing data as fallback
  const pocRecords = coord.socure_volume || (() => {
    const countryConfig = (disc.country_config || {}) as Record<string, any>
    const total = (poc.countries_in_scope || []).reduce((sum: number, code: string) => {
      const cc = countryConfig[code] || {}
      return sum + (parseInt(cc.records) || 0)
    }, 0)
    return total > 0 ? String(total) : (poc.record_count != null ? String(poc.record_count) : '')
  })()

  const w1 = contentWidth * 0.45
  const w2 = contentWidth * 0.25
  const w3 = contentWidth * 0.3
  simpleTable(
    ['Volume Type', 'Estimated Count', 'Notes'],
    [
      ['Records / applications in POC', pocRecords, coord.socure_volume_poc_notes || ''],
      ['Daily volume', coord.socure_volume_daily || '', coord.socure_volume_daily_notes || ''],
      ['Weekly volume', coord.socure_volume_weekly || '', coord.socure_volume_weekly_notes || ''],
      ['Monthly volume', coord.socure_volume_monthly || '', coord.socure_volume_monthly_notes || ''],
      ['Expected production monthly volume', coord.socure_volume_production || '', coord.socure_volume_production_notes || ''],
    ],
    [w1, w2, w3]
  )

  // ===== 4. Countries in Scope =====
  sectionHeader(4, 'Countries in Scope')
  subLabel('Please list all countries that will be included in the POC.')

  const countryCodes = poc.countries_in_scope || []
  const countryRows = countryCodes.map(code => {
    const name = COUNTRIES.find(c => c.code === code)?.name || code
    const countryNotes = coord[`socure_country_${code}_notes`] || ''
    return [name, 'Yes', countryNotes]
  })
  if (countryRows.length > 0) {
    simpleTable(['Country', 'Included in POC', 'Notes'], countryRows, [w1, w2, w3])
  } else {
    labeledField('Countries:', coord.socure_countries || null)
  }

  // ===== 5. Success Criteria =====
  sectionHeader(5, 'Success Criteria')
  subLabel('Success criteria must be clearly defined before kickoff.')

  // Fall back to existing success criteria
  let passRateTarget = coord.socure_target_pass_rate || ''
  if (!passRateTarget) {
    const countryConfig = (disc.country_config || {}) as Record<string, any>
    const targets = countryCodes.map(code => {
      const cc = countryConfig[code] || {}
      const name = COUNTRIES.find(x => x.code === code)?.name || code
      return cc.target_rate ? `${name}: >= ${cc.target_rate}%` : null
    }).filter(Boolean).join(', ')
    passRateTarget = targets || ''
  }

  simpleTable(
    ['Metric', 'Target', 'Notes'],
    [
      ['Pass rate', passRateTarget, coord.socure_target_pass_rate_notes || 'Example: >70%'],
      ['Match / approval rate', coord.socure_target_match_rate || '', coord.socure_target_match_rate_notes || ''],
      ['Manual review rate', coord.socure_target_manual_review || '', coord.socure_target_manual_review_notes || ''],
      ['False positive tolerance', coord.socure_target_false_positive || '', coord.socure_target_false_positive_notes || ''],
      ['Turnaround / response time', coord.socure_target_turnaround || '', coord.socure_target_turnaround_notes || ''],
    ],
    [w1, w2, w3]
  )

  labeledField('What outcome would be considered a successful POC:', coord.socure_success_outcome || coord.socure_success_criteria || null)
  labeledField('What outcome would be considered unsuccessful:', coord.socure_unsuccessful_outcome || null)

  // ===== 6. Data and PII Included =====
  sectionHeader(6, 'Data and PII Included')
  subLabel('Please confirm exactly what data elements will be provided as part of the POC.')

  const piiFields = (coord.socure_pii || []) as string[]
  const piiSet = new Set(piiFields)

  const PII_MAP: [string, string][] = [
    ['Full name', 'First Name'],
    ['Date of birth', 'Date of Birth'],
    ['Address', 'Physical Address Line 1'],
    ['Email', 'Email'],
    ['Phone number', 'Phone Number'],
    ['Government ID / document data', 'National ID'],
    ['IP address / device data', ''],
    ['Other', ''],
  ]

  const piiRows = PII_MAP.map(([label, legacyKey]) => {
    const included = coord[`socure_pii_${label.toLowerCase().replace(/[\s\/]+/g, '_')}`] ||
      (legacyKey && piiSet.has(legacyKey) ? 'Yes' : '')
    const notes = coord[`socure_pii_${label.toLowerCase().replace(/[\s\/]+/g, '_')}_notes`] || ''
    return [label, included || '', notes]
  })

  simpleTable(['Data Element / PII Field', 'Included', 'Notes'], piiRows, [w1, w2, w3])

  // ===== 7. Funnel Placement =====
  sectionHeader(7, 'Funnel Placement')
  subLabel('Please confirm where this data sits in the customer journey.')

  // Map from old funnel data
  const oldFunnel = (coord.socure_funnel || []) as string[]
  const isTopOfFunnel = coord.socure_funnel_top_of_funnel ||
    (oldFunnel.includes('Top of Funnel') ? 'Yes' : oldFunnel.length > 0 ? 'No' : '')

  labeledField('Is the data top of funnel?', isTopOfFunnel || null)
  labeledField('If no, at what stage is the user / applicant entering the flow?', coord.socure_funnel_stage || null)
  labeledField('Are these new applicants, existing customers, or both?', coord.socure_funnel_applicant_type || null)
  labeledField('Any pre-screening already performed before FrankieOne?', coord.socure_funnel_prescreening || null)

  // ===== 8. KYC Scorecard Requirements =====
  sectionHeader(8, 'KYC Scorecard Requirements')
  subLabel('Please provide the KYC scorecard that will be used for evaluation.')

  simpleTable(
    ['Scorecard Requirement', 'Response', 'Notes'],
    [
      ['1x1 or 2x2', coord.socure_scorecard_type || '', coord.socure_scorecard_type_notes || ''],
      ['Score bands / tiers defined', coord.socure_scorecard_bands || '', coord.socure_scorecard_bands_notes || ''],
      ['Pass / fail threshold defined', coord.socure_scorecard_passfail || '', coord.socure_scorecard_passfail_notes || ''],
      ['Manual review threshold defined', coord.socure_scorecard_manual_review || '', coord.socure_scorecard_manual_review_notes || ''],
      ['Exceptions / overrides documented', coord.socure_scorecard_overrides || '', coord.socure_scorecard_overrides_notes || ''],
    ],
    [w1, w2, w3]
  )

  // ===== 9. Additional Dependencies =====
  checkPage(30)
  sectionHeader(9, 'Additional Dependencies or Assumptions')

  labeledField('Integration dependencies:', coord.socure_dep_integration || null)
  labeledField('Data delivery format:', coord.socure_dep_data_format || null)
  labeledField('Testing environment needs:', coord.socure_dep_testing_env || null)
  labeledField('Compliance / legal considerations:', coord.socure_dep_compliance || null)
  labeledField('Other assumptions:', coord.socure_dep_other || null)

  // ===== Footer =====
  const pageCount = doc.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i)
    doc.setFontSize(7)
    doc.setTextColor(150, 150, 150)
    doc.text(
      `Generated by DealHub | ${new Date().toLocaleDateString('en-AU')} | Page ${i} of ${pageCount}`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 8,
      { align: 'center' }
    )
  }

  const filename = `FrankieOne-POC-Intake-${(poc.company_name || 'Socure').replace(/\s+/g, '-')}.pdf`
  doc.save(filename)
}
